/**
 * Master therapy scheduler — /therapy/master-schedule
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AppHeader from '../AppHeader';
import { useAuth } from '../../contexts/AuthContext';
import { therapyAPI, userManagementAPI } from '../../services/api';

const DAY_LABELS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

const formatTime = (t) => {
  if (!t) return '—';
  const [h, m] = t.split(':');
  const hour = Number(h);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const h12 = hour % 12 || 12;
  return `${h12}:${m?.slice(0, 2) || '00'} ${ampm}`;
};

export default function MasterTherapySchedulePage() {
  const { user, currentSite } = useAuth();
  const navigate = useNavigate();

  const [slots, setSlots] = useState([]);
  const [therapists, setTherapists] = useState([]);
  const [selectedTherapistId, setSelectedTherapistId] = useState('unassigned');
  const [serviceFilter, setServiceFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [editSlot, setEditSlot] = useState(null);
  const [editForm, setEditForm] = useState({ therapist: '', day_of_week: 0, start_time: '09:00' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { status: 'active' };
      const [slotsRes, usersRes] = await Promise.all([
        therapyAPI.listAllSlots(params),
        userManagementAPI.getUsers({ role: 'therapist', ...(currentSite?.id ? { site: currentSite.id } : {}) }),
      ]);
      setSlots(slotsRes.data || []);
      const list = Array.isArray(usersRes.data) ? usersRes.data : (usersRes.data?.results || []);
      setTherapists(list);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Failed to load schedule');
    } finally {
      setLoading(false);
    }
  }, [currentSite?.id]);

  useEffect(() => { load(); }, [load]);

  const filteredSlots = useMemo(() => {
    let list = slots;
    if (serviceFilter) list = list.filter((s) => s.service_type === serviceFilter);
    return list;
  }, [slots, serviceFilter]);

  const therapistIds = useMemo(() => {
    const ids = new Set();
    filteredSlots.forEach((s) => { if (s.therapist) ids.add(s.therapist); });
    return ids;
  }, [filteredSlots]);

  const slotsForView = useMemo(() => {
    if (selectedTherapistId === 'unassigned') {
      return filteredSlots.filter((s) => !s.therapist);
    }
    return filteredSlots.filter((s) => String(s.therapist) === String(selectedTherapistId));
  }, [filteredSlots, selectedTherapistId]);

  const slotsByDay = DAY_LABELS.map((_, dow) =>
    slotsForView.filter((s) => s.day_of_week === dow).sort((a, b) => (a.start_time > b.start_time ? 1 : -1))
  );

  const therapistLoad = useMemo(() => {
    const counts = {};
    filteredSlots.forEach((s) => {
      if (!s.therapist) return;
      counts[s.therapist] = (counts[s.therapist] || 0) + 1;
    });
    return counts;
  }, [filteredSlots]);

  const openEdit = (slot) => {
    setEditSlot(slot);
    setEditForm({
      therapist: slot.therapist || '',
      day_of_week: slot.day_of_week,
      start_time: slot.start_time?.slice(0, 5) || '09:00',
    });
  };

  const saveEdit = async () => {
    if (!editSlot) return;
    setSaving(true);
    try {
      await therapyAPI.updateSlot(editSlot.id, {
        therapist: editForm.therapist || null,
        day_of_week: editForm.day_of_week,
        start_time: editForm.start_time.length === 5 ? `${editForm.start_time}:00` : editForm.start_time,
      });
      setEditSlot(null);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const serviceTypes = [...new Set(slots.map((s) => s.service_type))].sort();

  if (!['superadmin', 'admin', 'editor'].includes(user?.role)) {
    return (
      <>
        <AppHeader title="Master Therapy Schedule" />
        <Container sx={{ py: 4 }}>
          <Alert severity="warning">Admin or editor access required.</Alert>
        </Container>
      </>
    );
  }

  return (
    <>
      <AppHeader title="Master Therapy Schedule" />
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Service</InputLabel>
            <Select label="Service" value={serviceFilter} onChange={(e) => setServiceFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {serviceTypes.map((st) => <MenuItem key={st} value={st}>{st}</MenuItem>)}
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary">
            {currentSite?.name || 'All sites'} · {filteredSlots.length} active slots
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Paper sx={{ maxHeight: '70vh', overflow: 'auto' }}>
                <List dense>
                  <ListItemButton selected={selectedTherapistId === 'unassigned'} onClick={() => setSelectedTherapistId('unassigned')}>
                    <ListItemText
                      primary="Unassigned"
                      secondary={`${filteredSlots.filter((s) => !s.therapist).length} slots`}
                    />
                  </ListItemButton>
                  {therapists.filter((t) => therapistIds.has(t.id)).map((t) => (
                    <ListItemButton key={t.id} selected={String(selectedTherapistId) === String(t.id)} onClick={() => setSelectedTherapistId(String(t.id))}>
                      <ListItemText
                        primary={`${t.first_name || ''} ${t.last_name || ''}`.trim() || t.email}
                        secondary={`${therapistLoad[t.id] || 0} slots`}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Paper>
            </Grid>

            <Grid item xs={12} md={9}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  {selectedTherapistId === 'unassigned' ? 'Unassigned slots' : 'Weekly calendar'}
                </Typography>
                <Grid container spacing={1}>
                  {DAY_LABELS.map((day, dow) => (
                    <Grid item xs={12} sm={6} md={2.4} key={day}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>{day}</Typography>
                      {slotsByDay[dow].length === 0 ? (
                        <Typography variant="caption" color="text.secondary">—</Typography>
                      ) : (
                        slotsByDay[dow].map((slot) => (
                          <Box
                            key={slot.id}
                            onClick={() => openEdit(slot)}
                            sx={{
                              p: 1, mb: 0.5, border: 1, borderColor: 'divider', borderRadius: 1, cursor: 'pointer',
                              '&:hover': { bgcolor: 'action.selected' },
                            }}
                          >
                            <Typography variant="caption" display="block" fontWeight={600}>
                              {formatTime(slot.start_time)} · {slot.service_label}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {(slot.student_names || []).join(', ') || 'No student'}
                            </Typography>
                            {slot.max_students > 1 && (
                              <Chip label={`Group ${slot.max_students}`} size="small" sx={{ mt: 0.5 }} />
                            )}
                            <Button size="small" sx={{ mt: 0.5, p: 0, minWidth: 0 }} onClick={(e) => {
                              e.stopPropagation();
                              if (slot.student) navigate(`/students/${slot.student}?tab=services`);
                            }}>
                              View student
                            </Button>
                          </Box>
                        ))
                      )}
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        )}

        <Dialog open={!!editSlot} onClose={() => !saving && setEditSlot(null)} maxWidth="xs" fullWidth>
          <DialogTitle>Edit slot</DialogTitle>
          <DialogContent dividers>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Therapist</InputLabel>
              <Select label="Therapist" value={editForm.therapist || ''} onChange={(e) => setEditForm((p) => ({ ...p, therapist: e.target.value }))}>
                <MenuItem value="">Unassigned</MenuItem>
                {therapists.map((t) => (
                  <MenuItem key={t.id} value={t.id}>{`${t.first_name || ''} ${t.last_name || ''}`.trim() || t.email}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Day</InputLabel>
              <Select label="Day" value={editForm.day_of_week} onChange={(e) => setEditForm((p) => ({ ...p, day_of_week: e.target.value }))}>
                {DAY_LABELS.map((d, i) => <MenuItem key={d} value={i}>{d}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField fullWidth size="small" type="time" label="Start time" InputLabelProps={{ shrink: true }}
              value={editForm.start_time} onChange={(e) => setEditForm((p) => ({ ...p, start_time: e.target.value }))} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => !saving && setEditSlot(null)}>Cancel</Button>
            <Button variant="contained" onClick={saveEdit} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
}
